import React from "react";
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@mui/material";
import { LocationGoLink } from "../../../../components/SectionGoButtons";
import {
  canNavigateSensorToAddresses,
  getSensorToAddressesUrl,
} from "../../../../utils/sectionNavigation";
import {
  formatAddressDisplay,
  getComplexLocationLabel,
} from "../../../../utils/addressFields";
import { makeStyles } from "@mui/styles";
import {
  TableCeilSort,
  VisibleContent,
  SkeletonLoadingTable
} from "../../../../components";
import formatDate from "../../../../utils/formatDate";

const TableCustom = ({ items, filter, totalPages, totalCount, isLoading, onSearch, onChangeFilter, onRowClick, drawerOpen }) => {
  const classes = useStyles();
  const [selectedId, setSelectedId] = React.useState(null);

  React.useEffect(() => {
    if (!drawerOpen) setSelectedId(null);
  }, [drawerOpen]);

  const handleChangePage = (e, p) => {
    onChangeFilter({ ...filter, page: p }, true);
  }
  const handleChangeSort = (values) => {
    onChangeFilter({ ...filter, ...values }, true);
  }
  const handleChangeLimit = (e) => {
    onChangeFilter({ ...filter, page: 1, limit: e.target.value }, true);
  }

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCeilSort name="complex_serial" sort_field={filter.sort_field} sort_direction={filter.sort_direction} onChange={handleChangeSort}>Серийный номер</TableCeilSort>
            <TableCeilSort name="model" sort_field={filter.sort_field} sort_direction={filter.sort_direction} onChange={handleChangeSort}>Модель</TableCeilSort>
            <TableCeilSort name="class" sort_field={filter.sort_field} sort_direction={filter.sort_direction} onChange={handleChangeSort}>Тип</TableCeilSort>
            <TableCeilSort name="location" sort_field={filter.sort_field} sort_direction={filter.sort_direction} onChange={handleChangeSort}>Местоположение</TableCeilSort>
            <TableCeilSort name="commissioning_date" sort_field={filter.sort_field} sort_direction={filter.sort_direction} sx={{pt: 0, pb: 0, lineHeight: 1.1}} onChange={handleChangeSort}>Дата ввода в<br/>эксплуатацию</TableCeilSort>
            <TableCeilSort name="verification_outdata" sort_field={filter.sort_field} sort_direction={filter.sort_direction} sx={{pt: 0, pb: 0, lineHeight: 1.1}} onChange={handleChangeSort}>Поверка<br/>действительна до</TableCeilSort>
          </TableRow>
        </TableHead>
        <TableBody>
          <VisibleContent visible={!isLoading}>
            {items.map((item) => {
              const rowId = item.id || item.complex_serial;
              return (
                <RowItem
                  key={rowId}
                  item={item}
                  isSelected={selectedId === rowId}
                  onClick={() => { setSelectedId(rowId); onRowClick(item); }}
                />
              );
            })}
          </VisibleContent>
          <VisibleContent visible={isLoading}>
            <SkeletonLoadingTable countRows={10} countColumns={6} />
          </VisibleContent>
        </TableBody>
      </Table>

      <Box className={classes.paginationBar}>
        <span className={classes.paginationInfo}>
          Найдено {totalCount.toLocaleString('ru-RU')} записей
        </span>
        <Box className={classes.paginationRight}>
          <Box className={classes.paginationLimit}>
            <span>Выводить по</span>
            <Select
              value={filter.limit}
              onChange={handleChangeLimit}
              size="small"
              sx={{ minWidth: 70, height: 32, fontSize: 14 }}
            >
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
          {totalPages > 1 && (
            <Box className={classes.paginationPages}>
              <span>Страница {filter.page} из {totalPages}</span>
              <Pagination
                count={totalPages}
                page={filter.page}
                onChange={handleChangePage}
                size="small"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const RowItem = React.memo(({ item, isSelected, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const addressesHref = getSensorToAddressesUrl(item);
  const showAddressesLink = canNavigateSensorToAddresses(item);

  const renderTypeBadge = () => {
    const type = item?.class || '';
    let sx = {};
    if (type === 'Стационарный') sx = { backgroundColor: '#E4FBF1', color: '#1CA673' };
    else if (type === 'Передвижной') sx = { backgroundColor: '#E3F2FD', color: '#2196F3' };
    else if (type === 'Мобильный') sx = { backgroundColor: '#FFF9E6', color: '#FFC107' };
    else sx = { backgroundColor: '#F4F4F5', color: '#71717A' };
    return <Chip label={type || 'Н/Д'} size="small" sx={{ fontWeight: 600, fontSize: 12, ...sx }} />;
  };

  return (
    <TableRow
      hover
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={isSelected ? "--selected" : ""}
      sx={{ cursor: "pointer" }}
    >
      <TableCell sx={{ whiteSpace: "nowrap" }}>{item?.complex_serial || "Н/Д"}</TableCell>
      <TableCell>{item?.model || 'Н/Д'}</TableCell>
      <TableCell>{renderTypeBadge()}</TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box component="span" sx={{ minWidth: 0 }}>{formatAddressDisplay(getComplexLocationLabel(item))}</Box>
          {showAddressesLink && addressesHref && (
            <LocationGoLink href={addressesHref} visible={isHovered} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(item?.commissioning_date)}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(item?.verification_outdata)}</TableCell>
    </TableRow>
  );
});
RowItem.displayName = 'RowItem';

const useStyles = makeStyles(() => ({
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
  },
  paginationInfo: { fontSize: 14, color: "#71717A", fontWeight: 400 },
  paginationRight: { display: "flex", alignItems: "center", gap: 24 },
  paginationLimit: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#71717A" },
  paginationPages: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#71717A",
    "& .MuiPagination-root .MuiPaginationItem-root": { minWidth: 28, height: 28 },
  },
}));

export default TableCustom
